/**
 * /request-consultation — the site's canonical consultation-request page.
 *
 * All layout / hero / trust content lives here; the actual form widget is
 * imported from `<ConsultationRequestForm>` so the same validated code path
 * powers the Google Ads landing page at /medical-consultation as well.
 */
import { CheckCircle2, Phone, Mail } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import ConsultationRequestForm from "../components/ConsultationRequestForm";

export default function RequestConsultation() {
  return (
    <div data-testid="request-consultation-page">
      <SEO
        title="Request a Consultation | MedicalPeptides.ai"
        description="Meet with a licensed VidaVital Medical provider to determine whether peptide therapy is appropriate for you. Complete the consultation request form and our care team will follow up."
        path="/request-consultation"
        breadcrumbs={[{ name: "Request Consultation", path: "/request-consultation" }]}
        noindex
      />

      <section className="vv-container py-12 md:py-16 grid md:grid-cols-2 gap-10 md:gap-16">
        <div className="flex flex-col justify-start">
          <Breadcrumbs items={[{ name: "Request Consultation", path: "/request-consultation" }]} />
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-gold-deep)] font-semibold mt-4 mb-3">
            Request Consultation
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight">
            Speak with our<br />clinical team.
          </h1>
          <p className="mt-6 text-stone-600 md:text-lg leading-relaxed italic max-w-md">
            Share a few details and a member of our team will follow up. Please do not send medical history through this form.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">General Inquiries</div>
              <a href="mailto:info@vidavitalmedical.com" className="text-[color:var(--vv-gold-deep)] hover:underline" data-testid="rc-general-email">
                info@vidavitalmedical.com
              </a>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Main Phone</div>
              <a href="tel:+18887738677" className="text-[color:var(--vv-gold-deep)] hover:underline" data-testid="rc-main-phone">
                (888) 773-8677
              </a>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Emergencies</div>
              <div className="text-stone-700">Call 911 or your local emergency services.</div>
            </div>
          </div>
        </div>

        <ConsultationRequestForm
          formName="consultation_request_form"
          sourceLabel="/request-consultation"
          showEmergencyBlurb
        />
      </section>

      <section className="vv-container pb-16 grid sm:grid-cols-3 gap-4 text-sm text-stone-700">
        <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[color:var(--vv-teal)]" /> Licensed medical provider evaluation</div>
        <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[color:var(--vv-teal)]" /> Personalized treatment plans</div>
        <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[color:var(--vv-teal)]" /> Multiple clinic locations + telehealth</div>
        <div className="flex items-center gap-2"><Phone size={16} className="text-[color:var(--vv-gold-deep)]" /> <a href="tel:+18887738677" className="underline">(888) 773-8677</a></div>
        <div className="flex items-center gap-2"><Mail size={16} className="text-[color:var(--vv-gold-deep)]" /> <a href="mailto:info@vidavitalmedical.com" className="underline">info@vidavitalmedical.com</a></div>
      </section>
    </div>
  );
}
